import { useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { AppLayout } from '../components/templates/AppLayout/AppLayout';
import { Header } from '../components/layout/Header';
import { Sidebar } from '../components/Sidebar';
import { PageContent } from '../components/templates/PageContent';
import { PageHeader } from '../components/templates/PageHeader';
import { LoadErrorState } from '../components/feedback/LoadErrorState/LoadErrorState';
import { TestSessionForm } from '../components/organisms/TestSessionForm';
import { Button } from '../components/atoms/Button/Button';
import type { GuidedPrefillEntry } from './GuidedTestSessionPage';
import { useToast } from '../stores/toastStore';
import { useTranslation } from '../i18n/useTranslation';
import { useVoiceJournal } from '../components/capture/CaptureContext';

interface TestSessionLocationState {
  returnTo?: string;
  invokerElementId?: string;
  /** Values of a guided session ("Finish"), shown here as the summary before saving. */
  prefill?: GuidedPrefillEntry[];
}

const DEFAULT_RETURN_TO = '/dashboard';

export function TestSessionPage() {
  const { t } = useTranslation();
  const { aquariumId } = useParams<{ aquariumId: string }>();
  const navigate = useNavigate();
  const voiceJournal = useVoiceJournal();
  const location = useLocation();
  const { showToast } = useToast();

  const state = (location.state ?? {}) as TestSessionLocationState;
  const returnTo = state.returnTo ?? DEFAULT_RETURN_TO;
  const invokerElementId = state.invokerElementId;

  const handleSuccess = useCallback(
    (_sessionId: string) => {
      showToast(t('testSession.toast.saved'), 'success');
      navigate(returnTo, {
        replace: true,
        state: { focusElementId: invokerElementId },
      });
    },
    [showToast, t, navigate, returnTo, invokerElementId],
  );

  const handleCancel = useCallback(() => {
    navigate(returnTo, {
      replace: true,
      state: { focusElementId: invokerElementId },
    });
  }, [navigate, returnTo, invokerElementId]);

  if (!aquariumId) {
    return (
      <AppLayout
        header={<Header />}
        sidebar={<Sidebar />}
        main={
          <PageContent width="narrow">
            <LoadErrorState
              title={t('testSession.error.missingAquarium.title')}
              message={t('testSession.error.missingAquarium.description')}
            />
          </PageContent>
        }
      />
    );
  }

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent width="narrow">
          <PageHeader
            title={state.prefill ? t('testSession.summaryTitle') : t('testSession.title')}
            description={state.prefill ? t('testSession.summarySubtitle') : t('testSession.subtitle')}
            actions={
              state.prefill || voiceJournal !== 'on' ? undefined : (
                <Button variant="secondary" onClick={() => navigate(`/aquariums/${encodeURIComponent(aquariumId)}/measurements/session/guided`)}>
                  {t('testSession.guided')}
                </Button>
              )
            }
          />
          <TestSessionForm aquariumId={aquariumId} onSuccess={handleSuccess} onCancel={handleCancel} prefill={state.prefill} />
        </PageContent>
      }
    />
  );
}

export default TestSessionPage;
